export type BusinessMutationErrorCode =
  | 'C-4001'
  | 'C-60003'
  | 'C-56888'
  | 'C-9846'
  | 'C-9857'
  | 'UNKNOWN_ERROR';

export class BusinessMutationApiError extends Error {
  readonly code: BusinessMutationErrorCode;

  constructor(code: BusinessMutationErrorCode, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'BusinessMutationApiError';
    this.code = code;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readErrorCode(value: unknown, depth = 0): string | null {
  if (depth > 3) return null;
  const record = asRecord(value);
  if (!record) return null;

  const response = asRecord(record.response);
  const data = asRecord(response?.data);
  const nestedError = asRecord(data?.error);
  const candidates = [
    record.errorCode,
    record.code,
    data?.errorCode,
    data?.code,
    nestedError?.errorCode,
    nestedError?.code,
  ];
  const direct = candidates.find(
    (candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0
  );
  return direct ?? readErrorCode(record.cause, depth + 1);
}

const COMMON_MESSAGES: Partial<Record<BusinessMutationErrorCode, string>> = {
  'C-60003': 'You do not have permission to manage business settings.',
  'C-56888': 'Your user account is not linked to this business.',
  'C-9846': 'Business data is unavailable. Refresh and try again.',
  'C-9857': 'Enter a valid phone number, including a country code for non-US numbers.',
};

export function normalizeBusinessMutationError(
  error: unknown,
  options: { invalidRequestMessage: string; fallbackMessage: string }
): BusinessMutationApiError {
  if (error instanceof BusinessMutationApiError) return error;

  const rawCode = readErrorCode(error);
  const code = (
    ['C-4001', 'C-60003', 'C-56888', 'C-9846', 'C-9857'].includes(rawCode ?? '')
      ? rawCode
      : 'UNKNOWN_ERROR'
  ) as BusinessMutationErrorCode;

  return new BusinessMutationApiError(
    code,
    code === 'C-4001'
      ? options.invalidRequestMessage
      : COMMON_MESSAGES[code] ?? options.fallbackMessage,
    { cause: error }
  );
}
